export const TZ = "America/Edmonton";
export const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const KINDS = [
  ["daily", "Every day"], ["weekdays", "Weekdays (Mon–Fri)"], ["weekly", "Once a week"],
  ["monthly", "Once a month"], ["hourly", "Every hour"], ["gap", "Every few minutes / hours"],
];
export const TIMER_KINDS = [["interval", "Every few minutes"], ["daily", "Every day"]];

const pad = (n) => String(n).padStart(2, "0");
const hm = (s) => `${pad(s.hour)}:${pad(s.minute)}`;
const every = (m) => (m % 1440 === 0 ? `Every ${m / 1440} day${m === 1440 ? "" : "s"}` : m % 60 === 0 ? `Every ${m / 60} h` : `Every ${m} min`);
const ord = (d) => `${d}${d === 1 || d === 21 ? "st" : d === 2 || d === 22 ? "nd" : d === 3 || d === 23 ? "rd" : "th"}`;

export function scheduleText(s) {
  if (!s) return "—";
  switch (s.kind) {
    case "daily": return `Every day at ${hm(s)}`;
    case "weekdays": return `Mon–Fri at ${hm(s)}`;
    case "weekly": return `Every ${DOW[s.dow]} at ${hm(s)}`;
    case "monthly": return `${ord(s.day)} of the month at ${hm(s)}`;
    case "hourly": return `Every hour at :${pad(s.minute)}`;
    case "gap": case "interval": return every(s.minutes);
    default: return s.text || s.kind;
  }
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The server sends local (Edmonton) ISO times — show their wall clock as-is, no browser time-zone maths. */
export function fmtAt(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || "");
  if (!m) return "—";
  const h = Number(m[4]);
  return `${MON[Number(m[2]) - 1]} ${Number(m[3])}, ${((h + 11) % 12) + 1}:${m[5]} ${h < 12 ? "AM" : "PM"}`;
}

/** "{'sent': 2, 'reason': 'x'}" (a job's returned dict) → "sent: 2 · reason: x". */
export function readable(r) {
  if (!(r || "").startsWith("{'")) return r;
  return r.replace(/^\{/, "").replace(/\}$/, "").replace(/'([^']*)':\s*/g, "$1: ").replace(/'([^']*)'/g, "$1").replace(/,\s(?=[\w ]+:\s)/g, " · ") || "ok";
}

export function resultTone(r, at) {
  if (!r) return ["text-slate-400", "Never ran"];
  if (r === "running" && at && Date.now() - new Date(at).getTime() > 15 * 60 * 1000) return ["text-amber-700", "Interrupted — the app restarted while it ran"];
  if (r === "running") return ["text-sky-700", "Running…"];
  if (r === "seeded") return ["text-slate-500", "Waiting for its first time"];
  if (r.startsWith("failed")) return ["text-rose-700", r];
  return ["text-emerald-700", readable(r)];
}

export const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const offFmt = {};
/** Minutes east of UTC for a time zone at a moment (e.g. -360 for MDT). */
function offsetMin(tz, d) {
  offFmt[tz] = offFmt[tz] || new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" });
  const v = offFmt[tz].formatToParts(d).find((p) => p.type === "timeZoneName")?.value || "GMT";
  const m = /GMT([+-])(\d{1,2}):?(\d{2})?/.exec(v);
  return m ? (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0)) : 0;
}
export const viewerDiffers = (d = new Date()) => offsetMin(VIEWER_TZ, d) !== offsetMin(TZ, d);
const VIEW_FMT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
const VIEW_HM = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" });

/** The same moment in the viewer's own time zone — null when it matches Mountain time. */
export function fmtViewer(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime()) || !viewerDiffers(d)) return null;
  return VIEW_FMT.format(d);
}

/** A Mountain wall-clock time (today) in the viewer's zone, e.g. "6:00 AM EDT" or "1:00 AM GMT+2 (next day)". */
export function mountainToViewer(hour, minute) {
  if (hour == null || minute == null) return null;
  const now = new Date();
  const mt = new Date(now.getTime() + offsetMin(TZ, now) * 60000);
  const guess = Date.UTC(mt.getUTCFullYear(), mt.getUTCMonth(), mt.getUTCDate(), hour, minute);
  const utc = guess - offsetMin(TZ, new Date(guess)) * 60000;
  const d = new Date(utc);
  if (!viewerDiffers(d)) return null;
  const dayOf = (off) => Math.floor((utc + off * 60000) / 86400000);
  const shift = dayOf(offsetMin(VIEWER_TZ, d)) - dayOf(offsetMin(TZ, d));
  return `${VIEW_HM.format(d)}${shift > 0 ? " (next day)" : shift < 0 ? " (day before)" : ""}`;
}

export function fmtDuration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${Math.round(s % 60)} s`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

export const SOURCE = { cron: "Platform trigger", apscheduler: "In-app driver", manual: "By hand", timer: "Timer" };
